/**
 * Frontend wrapper around the create-vendor-assignment Edge Function.
 *
 * Triggers the full assignment-send flow: validates the vendor + job,
 * generates accept/decline tokens, inserts a vendor_assignments row,
 * schedules the two reminder/escalation tasks, updates field_jobs.vendor_id,
 * and sends the assignment email to the vendor. Server-side end-to-end —
 * raw tokens never reach the browser.
 *
 * Mirrors the {data, error} shape of src/lib/email.ts and the error-body
 * extraction idiom Settings.tsx uses for create-invite, so callers get a
 * useful Error.message instead of the generic gateway "Edge Function
 * returned a non-2xx status code".
 *
 * The success shape is { assignmentId, warning? }:
 *   - assignmentId always populated on success.
 *   - warning populated when the assignment was created but the email
 *     send failed (e.g. RESEND_API_KEY missing, Resend rejected). The
 *     reminder handler will still nudge the vendor at the configured
 *     reminder window, so this is a soft warning not a hard failure.
 */

import { supabase } from './supabase'

export type CreateVendorAssignmentInput = {
  fieldJobId: string
  vendorId: string
  /** Optional vendor-facing price. Margin-safe — never auto-set from field_jobs.total. */
  budgetOffered?: number | null
}

export type CreateVendorAssignmentResult = {
  /** The new vendor_assignments.id. */
  assignmentId: string
  /** Set when DB writes succeeded but the assignment email failed to send. */
  warning?: string
}

/**
 * Always returns a {data, error} tuple — never throws. Callers should
 * check error before using data.
 */
export async function createVendorAssignment(
  input: CreateVendorAssignmentInput,
): Promise<{ data: CreateVendorAssignmentResult | null; error: Error | null }> {
  const { data, error } = await supabase.functions.invoke<{ id: string; warning?: string }>(
    'create-vendor-assignment',
    {
      body: {
        field_job_id: input.fieldJobId,
        vendor_id: input.vendorId,
        budget_offered: input.budgetOffered ?? null,
      },
    },
  )

  if (error) {
    // supabase.functions.invoke surfaces non-2xx as an error but doesn't
    // pull the response body by default. Extract { error } from the JSON
    // body for a useful message (same idiom as src/lib/email.ts and the
    // create-invite caller in Settings.tsx).
    const ctx = (error as { context?: { text?: () => Promise<string> } }).context
    let message = error.message
    if (ctx && typeof ctx.text === 'function') {
      try {
        const text = await ctx.text()
        const parsed = JSON.parse(text)
        message = parsed?.error || parsed?.message || message
      } catch {
        /* keep default */
      }
    }
    return { data: null, error: new Error(message) }
  }

  if (!data?.id) {
    return { data: null, error: new Error('No assignment id returned') }
  }

  return {
    data: { assignmentId: data.id, warning: data.warning },
    error: null,
  }
}
